import { useCallback, useEffect, useState } from "react";
import { SortColumn } from "react-data-grid";
import { useNavigate } from "react-router-dom";

import { ArrowheadRightDarkIcon } from "../../../assets";
import { OrganisationTypeConstants } from "../../../constants";
import { CursorChangeProps, ResultType } from "../../../models";
import { getActivityAggregate, searchActivities, SearchActivitiesResponse } from "../../../service/query";
import { ResultData, Status } from "../../../service/Shared";
import { useAuth } from "../../../useAuth";
import { flattenObject } from "../../../utils";
import { getActivityDashboardTabRoute, getActivityViewRoute, getProjectDetailsByUuid } from "../../../utils/routes";
import {
  ChartData,
  DataGridButtonLinkCellFormatterData,
  DataGridColumnDefinition,
  DataGridLinkCellFormatterData,
  dataGridMapFilterCriteria,
  Toast,
} from "../../../widget";
import { IconCellFormatterData } from "../../../widget/data/DataGrid/models";

interface UseActivitiesReturnData {
  activityTypeChartData: ChartData;
  activityStatusChartData: ChartData;
  activityCodeChartData: ChartData;
  activityDeveloperChartData: ChartData;
  columns: DataGridColumnDefinition[];
  isLoading: boolean;
  defaultSortingCriteria: SortColumn[];
  onChange: ({ filtering, paging, sorting }: CursorChangeProps) => Promise<{
    resultData: ResultData[];
    paging: {
      pageSize: number;
      totalCount: number;
      startCursor: string;
      endCursor: string;
      hasNextPage: boolean;
      hasPreviousPage: boolean;
    };
  }>;
}

const pageSize = 10;

const columns: DataGridColumnDefinition[] = [
  { key: "developerDisplayName", name: "Developer", dataType: "string" },
  {
    name: "Project",
    key: "projectLink",
    dataType: "string",
    formatter: "link",
    filterable: true,
    sortable: true,
  },
  {
    name: "Activity",
    key: "activityLink",
    dataType: "string",
    formatter: "buttonLink",
    filterable: true,
    sortable: true,
  },
  {
    name: "Variant",
    key: "variant",
    dataType: "string",
    alignment: "center",
    formatter: "align",
    filterable: true,
    sortable: true,
  },
  {
    name: "Status",
    key: "status",
    dataType: "string",
    formatter: "projectActivitiesStatusPill",
    minWidth: 240,
    alignment: "center",
    filterable: true,
    sortable: true,
  },
  {
    name: "Last updated",
    key: "currentVersion.updatedAt",
    dataType: "Date",
    formatter: "dateOnly",
    alignment: "center",
    filterable: false,
    sortable: true,
  },
  {
    name: "Updated by",
    key: "currentVersion.updatedByUser.fullName",
    dataType: "string",
    alignment: "center",
    formatter: "align",
    filterable: true,
    sortable: true,
  },
  {
    name: "More details",
    key: "activityHistoryLink",
    dataType: "string",
    formatter: "icon",
    alignment: "center",
    filterable: false,
    sortable: false,
  },
];

export const useActivities = (): UseActivitiesReturnData => {
  const navigate = useNavigate();

  const { currentUserType, currentAssetManagerUuid } = useAuth();

  const [isLoading, setIsLoading] = useState(true);
  const [activityTypeChartData, setActivityTypeChartData] = useState<ChartData>({});
  const [activityStatusChartData, setActivityStatusChartData] = useState<ChartData>({});
  const [activityCodeChartData, setActivityCodeChartData] = useState<ChartData>({});
  const [activityDeveloperChartData, setActivityDeveloperChartData] = useState<ChartData>({});

  const defaultSortingCriteria: SortColumn[] = [{ columnKey: "currentVersion.updatedAt", direction: "DESC" }];

  const formatData = useCallback(
    (responseData: SearchActivitiesResponse | undefined): ResultData[] =>
      responseData?.results?.map((el) => {
        const result = flattenObject(el);

        result.projectLink = {
          text: el.project ? el.project.displayName : el.group?.displayName,
          // eslint-disable-next-line prettier/prettier, @typescript-eslint/no-non-null-assertion
          to: el.project ? getProjectDetailsByUuid(el.project.uuid, OrganisationTypeConstants.ASSET_MANAGER) : "",
        } as DataGridLinkCellFormatterData;

        result.developerDisplayName = el.project ? el.project.developer.displayName : el.group?.developer?.displayName;

        result.activityLink = {
          text: el.activityDefinition.displayName,
          action: () => {
            if (el.currentVersion) {
              navigate(
                getActivityViewRoute(
                  el.currentVersion.uuid,
                  currentUserType,
                  el.project ? `projectUuid=${el.project.uuid}` : ""
                )
              );
            } else {
              Toast.error({
                message: `This activity has not yet been published. Once ${el.createdByUser.fullName} has published the activity, you can view the activity.`,
              });
            }
          },
        } as DataGridButtonLinkCellFormatterData;

        result.activityHistoryLink = {
          action: () =>
            navigate(
              getActivityDashboardTabRoute(
                el.uuid,
                "documents",
                currentUserType,
                el.project ? `projectUuid=${el.project.uuid}` : ""
              )
            ),
          icon: <ArrowheadRightDarkIcon width={12} height={12} />,
        } as IconCellFormatterData;

        return result;
      }) || [],
    []
  );

  const onChange = async ({ filtering, paging, sorting }: CursorChangeProps): Promise<ResultType> => {
    const filterCriteria = dataGridMapFilterCriteria(filtering);
    if (filterCriteria.projectLink) {
      filterCriteria.project = {
        displayName: filterCriteria.projectLink,
      };
      filterCriteria.projectLink = undefined;
    }
    if (filterCriteria.activityLink) {
      filterCriteria.activityDefinition = {
        displayName: filterCriteria.activityLink,
      };
      filterCriteria.activityLink = undefined;
    }
    if (filterCriteria.developerDisplayName) {
      filterCriteria.project = {
        ...filterCriteria.project,
        developer: { displayName: filterCriteria.developerDisplayName },
      };
      filterCriteria.developerDisplayName = undefined;
    }

    let data: ResultType = {
      resultData: [],
      paging: {
        pageSize,
        totalCount: 0,
        startCursor: "",
        endCursor: "",
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };

    const sortKeyMap: Record<string, string> = {
      projectLink: "project.displayName",
      activityLink: "activityDefinition.displayName",
      developerDisplayName: "project.developer.displayName",
    };

    await searchActivities({
      paging: {
        beforeCursor: paging.beforeCursor || null,
        afterCursor: paging.afterCursor || null,
        limit: pageSize,
      },
      /* eslint-disable @typescript-eslint/no-explicit-any */
      sort: sorting.map((s: { key: any; direction: any }) => ({
        key: sortKeyMap[s.key] || s.key,
        direction: s.direction,
      })),
      /* eslint-enable */
      filter: { results: filterCriteria },
    })
      .then(async (response) => {
        data = {
          resultData: formatData(response.data),
          paging: {
            startCursor: response.data?.paging?.startCursor || "",
            endCursor: response.data?.paging?.endCursor || "",
            pageSize,
            totalCount: response.data?.paging?.total || 0,
            hasNextPage: response.data?.paging?.hasNextPage || false,
            hasPreviousPage: response.data?.paging?.hasPreviousPage || false,
          },
        };
      })
      .finally(() => {
        setIsLoading(false);
      });

    return data;
  };

  const fetchData = useCallback(async (): Promise<void> => {
    if (currentAssetManagerUuid) {
      const [activityTypeRes, activityStatusRes, activityCodeRes, activityDeveloperRes] = await Promise.all([
        getActivityAggregate({
          groupBy: "activityDefinition.displayName",
          filterBy: "assetManager",
          filterOperator: "eq",
          aggregate: "activity.id",
          aggregation: "count",
          filterValue: currentAssetManagerUuid,
        }),
        getActivityAggregate({
          groupBy: "activity.status",
          filterBy: "assetManager",
          filterOperator: "eq",
          aggregate: "activity.id",
          aggregation: "count",
          filterValue: currentAssetManagerUuid,
        }),
        getActivityAggregate({
          groupBy: "activityDefinition.standard.displayName",
          filterBy: "assetManager",
          filterOperator: "eq",
          aggregate: "activity.id",
          aggregation: "count",
          filterValue: currentAssetManagerUuid,
        }),
        getActivityAggregate({
          groupBy: "developer",
          filterBy: "assetManager",
          filterOperator: "eq",
          aggregate: "activity.id",
          aggregation: "count",
          filterValue: currentAssetManagerUuid,
        }),
      ]);

      if (activityTypeRes.status === Status.Success && activityTypeRes.data) {
        setActivityTypeChartData(
          activityTypeRes.data.reduce<ChartData>((previous, current) => {
            // eslint-disable-next-line no-param-reassign
            previous[current.key] = +current.value;
            return previous;
          }, {})
        );
      }

      if (activityStatusRes.status === Status.Success && activityStatusRes.data) {
        setActivityStatusChartData(
          activityStatusRes.data.reduce<ChartData>((previous, current) => {
            // eslint-disable-next-line no-param-reassign
            previous[current.key] = +current.value;
            return previous;
          }, {})
        );
      }

      if (activityCodeRes.status === Status.Success && activityCodeRes.data) {
        setActivityCodeChartData(
          activityCodeRes.data.reduce<ChartData>((previous, current) => {
            // eslint-disable-next-line no-param-reassign
            previous[current.key] = +current.value;
            return previous;
          }, {})
        );
      }

      if (activityDeveloperRes.status === Status.Success && activityDeveloperRes.data) {
        setActivityDeveloperChartData(
          activityDeveloperRes.data.reduce<ChartData>((previous, current) => {
            // eslint-disable-next-line no-param-reassign
            previous[current.key] = +current.value;
            return previous;
          }, {})
        );
      }
    }
  }, []);

  useEffect(() => {
    fetchData().finally(() => setIsLoading(false));
  }, [fetchData]);

  return {
    activityTypeChartData,
    activityStatusChartData,
    activityCodeChartData,
    activityDeveloperChartData,
    columns,
    isLoading,
    defaultSortingCriteria,
    onChange,
  };
};
